import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/use-api';
import { relativeTime } from '../lib/api';
import { Empty, ErrorBanner, Loading, Status } from '../components/ui';

interface DeviceView {
  deviceId: string;
  status: string;
  serialNumber: string | null;
  firmwareVersion: string | null;
  lastCheckinAt: string | null;
  warranty: { expiresAt: string | null; active: boolean };
}

interface Lifecycle {
  statuses: string[];
}

/** Wireframe C2 — the fleet-wide device registry. */
export function DeviceRegistry() {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState({ status: '', search: '' });

  const query = new URLSearchParams({ limit: '100' });
  if (applied.status) query.set('status', applied.status);
  if (applied.search) query.set('search', applied.search);

  const devices = useApi<{ items: DeviceView[]; total: number }>(`/internal/devices?${query}`);
  const lifecycle = useApi<Lifecycle>('/internal/devices/lifecycle');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Device registry</h1>
          <p>Every device ID ever created, wherever it is now.</p>
        </div>
      </div>

      <ErrorBanner error={devices.error} />

      <div className="filters">
        <div className="field">
          <label htmlFor="status">Lifecycle status</label>
          <select id="status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {(lifecycle.data?.statuses ?? []).map((value) => (
              <option key={value} value={value}>
                {value.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="search">Device ID or serial</label>
          <input
            id="search"
            value={search}
            placeholder="NG-4K7Q-H2M9"
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setApplied({ status, search });
            }}
          />
        </div>
        <button onClick={() => setApplied({ status, search })}>Apply</button>
        <button
          className="secondary"
          onClick={() => {
            setStatus('');
            setSearch('');
            setApplied({ status: '', search: '' });
          }}
        >
          Clear
        </button>
      </div>

      <section className="panel">
        <div className="panel-head">
          <h2>{devices.data?.total ?? 0} devices</h2>
        </div>
        {devices.loading ? (
          <Loading />
        ) : devices.data?.items.length ? (
          <table>
            <thead>
              <tr>
                <th>Device ID</th>
                <th>Status</th>
                <th>Serial</th>
                <th>Firmware</th>
                <th>Warranty</th>
                <th>Last check-in</th>
              </tr>
            </thead>
            <tbody>
              {devices.data.items.map((device) => (
                <tr key={device.deviceId}>
                  <td className="mono">
                    <Link to={`/devices/${device.deviceId}`}>{device.deviceId}</Link>
                  </td>
                  <td>
                    <Status value={device.status} />
                  </td>
                  <td className="mono muted">{device.serialNumber ?? '—'}</td>
                  <td className="mono">{device.firmwareVersion ?? '—'}</td>
                  <td>
                    {device.warranty.expiresAt ? (
                      <span className={`badge ${device.warranty.active ? 'ok' : 'danger'}`}>
                        {device.warranty.active ? daysLeft(device.warranty.expiresAt) : 'expired'}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="muted">{relativeTime(device.lastCheckinAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No devices match those filters.</Empty>
        )}
      </section>
    </>
  );
}

function daysLeft(expiresAt: string): string {
  const days = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000);
  return `${days} days left`;
}
